/**
 * Phase 7: Projects API client
 * Typed API client for all project-related endpoints
 */

import { api } from '@/lib/api';
import { PLATFORM_TRASH_RETENTION_DAYS } from '@/lib/platformRetention';
import { ProjectStatus, ProjectPriority, ProjectRiskLevel } from './types';

/** How workspace / portfolio / template defaults were applied (backend `governance_source`). */
export type ProjectGovernanceSource = 'USER' | 'TEMPLATE' | 'PORTFOLIO' | 'LEGACY';

/**
 * @deprecated OV-1 Phase A — `governanceSource === 'TEMPLATE'` means template inheritance,
 * not that governance/gates are live on this project. Always returns false.
 * Use `projectHasActiveGateDefinitions` (from plan `phases[].gate.definitionExists`) for badges.
 */
export function projectShowsGovernanceIndicator(
  _project: Pick<ProjectDetail, 'governanceSource'> | null | undefined,
): boolean {
  return false;
}

function readGovernanceSourceFromPayload(raw: unknown): ProjectGovernanceSource | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const v = r.governanceSource ?? r.governance_source;
  if (typeof v !== 'string' || !v.trim()) return undefined;
  return v.trim() as ProjectGovernanceSource;
}

import {
  normalizeColumnConfig,
  type ColumnConfig,
  toFlatColumnConfig,
} from '@/features/templates/columnConfig.model';

/** Ensures camelCase governanceSource for list/detail payloads (snake_case tolerant). */
function readColumnConfigFromPayload(raw: unknown): ColumnConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const cc = r.columnConfig ?? r.column_config;
  if (!cc || typeof cc !== 'object' || Array.isArray(cc)) return null;
  const nested = normalizeColumnConfig(cc);
  if (
    Object.keys(nested.columns).length === 0 &&
    !nested.defaultView &&
    !nested.visibleTabs
  ) {
    return null;
  }
  return nested;
}

function readOptionalBoolean(
  raw: object,
  camel: string,
  snake: string,
): boolean | undefined {
  const r = raw as Record<string, unknown>;
  const v = r[camel] ?? r[snake];
  return typeof v === 'boolean' ? v : undefined;
}

function projectDetailWithGovernanceSource(raw: unknown): ProjectDetail {
  if (raw == null || typeof raw !== 'object') return raw as ProjectDetail;
  const base = raw as ProjectDetail;
  const gs = readGovernanceSourceFromPayload(raw);
  const columnConfig = readColumnConfigFromPayload(raw);
  const baselinesEnabled = readOptionalBoolean(
    raw,
    'baselinesEnabled',
    'baselines_enabled',
  );
  return {
    ...base,
    ...(gs !== undefined ? { governanceSource: gs } : {}),
    ...(columnConfig !== null ? { columnConfig } : {}),
    ...(baselinesEnabled !== undefined ? { baselinesEnabled } : {}),
  };
}

export interface ProjectSummary {
  id: string;
  name: string;
  workspaceId: string;
  methodology: string;
  status: ProjectStatus;
  ownerId?: string;
  phasesCount: number;
  tasksCount: number;
  risksCount: number;
  kpisCount: number;
  startDate?: string;
  endDate?: string;
  estimatedEndDate?: string;
  progress: number;
  riskScore: number;
  priority: ProjectPriority;
  riskLevel: ProjectRiskLevel;
  description?: string;
  createdAt: string;
  updatedAt: string;
  /** Present when backend sends governance_source / governanceSource. */
  governanceSource?: ProjectGovernanceSource | null;
}

export interface ProjectDetail extends ProjectSummary {
  definitionOfDone?: string[];
  // Budget & Cost Lite
  budget?: number;
  actualCost?: number;
  currency?: string;
  flatLaborRatePerHour?: number;
  costTrackingEnabled?: boolean;
  /** Persisted projects.baselines_enabled. Absent means BaselinePanel must hide. */
  baselinesEnabled?: boolean;
  // Template Enforcement
  iterationsEnabled?: boolean;
  estimationMode?: 'points_only' | 'hours_only' | 'both';
  defaultIterationLengthDays?: number;
  // Phase 3 (Template Center): per-project team membership
  teamMemberIds?: string[] | null;
  projectManagerId?: string | null;
  /** LIFECYCLE-1b. Absent until the project payload includes it. */
  state?: 'DRAFT' | 'ACTIVE' | 'CLOSING' | 'CLOSED';
  /** P-2 / A8b: nested column toggles + view routing (normalized from flat JSONB). */
  columnConfig?: ColumnConfig | null;
}

export interface Task {
  id: string;
  projectId: string;
  taskNumber: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assignedTo?: string;
  assignee?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  estimatedHours: number;
  actualHours: number;
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Risk {
  id: string;
  projectId: string;
  organizationId: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  status: string;
  detectedAt: string;
  source?: string;
  evidence?: any;
  mitigation?: any;
  createdAt: string;
  updatedAt: string;
}

export interface KPI {
  id: string;
  projectId: string;
  metricDate: string;
  metricType: string;
  metricCategory: string;
  metricValue: number;
  metricUnit?: string;
  metricMetadata?: {
    source: string;
    confidence: number;
    trend: 'improving' | 'stable' | 'deteriorating';
    benchmark?: number;
    target?: number;
    direction?: 'higher_is_better' | 'lower_is_better';
    additionalData?: any;
  };
  notes?: string;
  recordedBy: string;
  createdAt: string;
}

export interface UpdateProjectSettingsDto {
  name?: string;
  description?: string;
  projectManagerId?: string;
  startDate?: string;
  endDate?: string;
  estimatedEndDate?: string;
  status?: ProjectStatus;
  priority?: ProjectPriority;
  definitionOfDone?: string[];
}

/**
 * Projects API Service
 */
export const projectsApi = {
  /**
   * Get all projects (optionally filtered by workspace)
   */
  async getProjects(params?: {
    workspaceId?: string;
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{ projects: ProjectDetail[]; total: number; page: number; totalPages: number }> {
    // api.ts response interceptor already unwraps { data: T } envelope
    const result: any = await api.get('/projects', { params });
    // Handle both unwrapped and raw shapes defensively
    const payload = result?.projects ? result : (result?.data ?? result);
    if (payload?.projects && Array.isArray(payload.projects)) {
      return {
        ...payload,
        projects: payload.projects.map((p: unknown) => projectDetailWithGovernanceSource(p)),
      };
    }
    return payload?.projects
      ? payload
      : {
          projects: Array.isArray(payload)
            ? payload.map((p: unknown) => projectDetailWithGovernanceSource(p))
            : [],
          total: 0,
          page: 1,
          totalPages: 0,
        };
  },

  /**
   * Get a single project by ID
   */
  async getProject(id: string): Promise<ProjectDetail | null> {
    // api.ts response interceptor already unwraps { data: T } envelope,
    // so the resolved value IS the project object directly.
    const result = await api.get(`/projects/${id}`);
    if (result == null || typeof result !== 'object') return null;
    return projectDetailWithGovernanceSource(result);
  },

  /**
   * PATCH /projects/:id — persists fields accepted by UpdateProjectDto,
   * including budget (the /settings route does not apply that column).
   */
  async updateProject(
    id: string,
    patch: { budget?: number },
  ): Promise<ProjectDetail> {
    const result: any = await api.patch(`/projects/${id}`, patch);
    const data = result?.data ?? result;
    return projectDetailWithGovernanceSource(data);
  },

  /**
   * PUT /work/projects/:id/actual-cost — the only write for projects.actual_cost.
   * PATCH /projects/:id refuses actualCost.
   */
  async setActualCost(
    id: string,
    actualCost: number | null,
    workspaceId: string,
  ): Promise<void> {
    await api.put(`/work/projects/${id}/actual-cost`, { actualCost }, {
      headers: { 'x-workspace-id': workspaceId },
    });
  },

  /**
   * Get project summary with counts
   */
  async getProjectSummary(id: string): Promise<ProjectSummary> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.get(`/projects/${id}/summary`);
    return result?.data ?? result;
  },

  /**
   * Get project tasks
   * PART 1 Step 2: Use /work/tasks endpoint with projectId query param
   */
  async getProjectTasks(id: string): Promise<Task[]> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.get(`/work/tasks?projectId=${id}`);
    return Array.isArray(result) ? result : (result?.data ?? result ?? []);
  },

  /**
   * Get project risks
   */
  async getProjectRisks(id: string): Promise<Risk[]> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.get(`/projects/${id}/risks`);
    return Array.isArray(result) ? result : (result?.data ?? result ?? []);
  },

  /**
   * Get project KPIs (legacy - returns KPI metrics)
   */
  async getProjectKPIs(id: string): Promise<KPI[]> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.get(`/projects/${id}/kpis`);
    return Array.isArray(result) ? result : (result?.data ?? result ?? []);
  },

  /**
   * Get project KPI settings (available KPIs and active KPI IDs)
   */
  async getProjectKpiSettings(id: string): Promise<{
    availableKPIs: Array<{
      id: string;
      name: string;
      description?: string;
      type?: string;
      calculationMethod?: string;
      unit?: string;
      [key: string]: any;
    }>;
    activeKpiIds: string[];
  }> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.get(`/projects/${id}/kpis`);
    const data = result?.availableKPIs ? result : (result?.data ?? result);
    return {
      availableKPIs: data?.availableKPIs || [],
      activeKpiIds: data?.activeKpiIds || [],
    };
  },

  /**
   * Update project KPI activation state
   */
  async updateProjectKpiSettings(
    id: string,
    activeKpiIds: string[],
  ): Promise<{
    availableKPIs: Array<{
      id: string;
      name: string;
      [key: string]: any;
    }>;
    activeKpiIds: string[];
  }> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.patch(`/projects/${id}/kpis`, {
      activeKpiIds,
    });
    const data = result?.availableKPIs ? result : (result?.data ?? result);
    return {
      availableKPIs: data?.availableKPIs || [],
      activeKpiIds: data?.activeKpiIds || [],
    };
  },

  /**
   * Update project settings
   */
  async updateProjectSettings(
    id: string,
    settings: UpdateProjectSettingsDto,
  ): Promise<ProjectDetail> {
    // api.ts interceptor already unwraps { data: T }
    const result: any = await api.patch(`/projects/${id}/settings`, settings);
    return result?.data ?? result;
  },

  /**
   * P-2 / B9: Update per-project column config (includes `visibleTabs` for tab bar).
   * Accepts nested ColumnConfig; wires flat JSONB to the API.
   */
  async updateColumnConfig(
    id: string,
    columnConfig: ColumnConfig | Record<string, unknown>,
  ): Promise<ColumnConfig> {
    const flat = toFlatColumnConfig(normalizeColumnConfig(columnConfig));
    const result: any = await api.patch(`/projects/${id}/column-config`, {
      columnConfig: flat,
    });
    const data = result?.columnConfig ?? result?.data?.columnConfig ?? result?.data ?? result;
    return normalizeColumnConfig(data && typeof data === 'object' ? data : flat);
  },

  /**
   * Phase 3 (Template Center): Get the explicit per-project team.
   * PM is always implicitly included in the returned set.
   */
  async getProjectTeam(
    id: string,
  ): Promise<{ teamMemberIds: string[]; projectManagerId: string | null }> {
    const result: any = await api.get(`/projects/${id}/team`);
    const data = result?.data ?? result;
    return {
      teamMemberIds: Array.isArray(data?.teamMemberIds) ? data.teamMemberIds : [],
      projectManagerId: data?.projectManagerId ?? null,
    };
  },

  /**
   * Phase 3 (Template Center): Update the project team.
   * Backend validates that all IDs are active workspace members.
   * PM is always retained — cannot be removed via this endpoint.
   */
  async updateProjectTeam(
    id: string,
    teamMemberIds: string[],
  ): Promise<{ teamMemberIds: string[] }> {
    const result: any = await api.patch(`/projects/${id}/team`, { teamMemberIds });
    const data = result?.data ?? result;
    return {
      teamMemberIds: Array.isArray(data?.teamMemberIds) ? data.teamMemberIds : [],
    };
  },

  /**
   * Phase 4 / TC-F3: Save this project as an ORG-scoped template (admin-only).
   * Manifest include* flags are optional; omitted flags default to true on the backend
   * (duplicate-project path). Dialog sends explicit TC-F3 defaults.
   */
  async saveProjectAsTemplate(
    id: string,
    payload: {
      name?: string;
      description?: string;
      category?: string;
      includeStatuses?: boolean;
      includeFields?: boolean;
      includeViews?: boolean;
      includePhases?: boolean;
      includeSampleTasks?: boolean;
      includeDocuments?: boolean;
      includeGovernance?: boolean;
    },
  ): Promise<{
    id: string;
    name: string;
    templateScope: string;
    workspaceId: string;
    createdById: string;
    createdAt: string;
    notKept?: Array<{ part: string; count: number }>;
  }> {
    const result: any = await api.post(`/projects/${id}/save-as-template`, payload);
    return result?.data ?? result;
  },

  /**
   * Phase 4.5 (Template Center): Duplicate project via the canonical
   * save-as-template → instantiate plumbing. Live execution data is excluded
   * by Option B snapshot rules.
   */
  async duplicateProject(
    id: string,
    payload: { newName: string },
  ): Promise<{
    sourceProjectId: string;
    newProjectId: string;
    newProjectName: string;
    phaseCount: number;
    taskCount: number;
  }> {
    const result: any = await api.post(`/projects/${id}/duplicate`, payload);
    return result?.data ?? result;
  },

  /**
   * Archive project — same soft-remove as delete (Archive & delete retention).
   */
  async archiveProject(id: string): Promise<{ trashRetentionDays: number }> {
    const body = (await api.post(`/projects/${id}/archive`, {})) as {
      id?: string;
      trashRetentionDays?: number;
    };
    const trashRetentionDays =
      typeof body?.trashRetentionDays === 'number' && body.trashRetentionDays > 0
        ? body.trashRetentionDays
        : PLATFORM_TRASH_RETENTION_DAYS;
    return { trashRetentionDays };
  },

  /**
   * Delete project (soft delete; trashRetentionDays from platform default)
   */
  async deleteProject(id: string): Promise<{ trashRetentionDays: number }> {
    const body = (await api.delete(`/projects/${id}`)) as {
      trashRetentionDays?: number;
    };
    const trashRetentionDays =
      typeof body?.trashRetentionDays === 'number' && body.trashRetentionDays > 0
        ? body.trashRetentionDays
        : PLATFORM_TRASH_RETENTION_DAYS;
    return { trashRetentionDays };
  },
};

